import "@/App.css";
import { useState, useEffect, useRef } from "react";
import { motion, animate } from "framer-motion";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { ArrowRight, CalendarDays, Quote, UserCheck, CalendarCheck, MessagesSquare, Building2, Sparkles, HeartHandshake, ShieldCheck, Wallet, TrendingUp, Users, Info, Percent, Check, Compass, Play } from "lucide-react";
import { SmoothScroll } from "@/components/SmoothScroll";
import { ScheduleProvider, useSchedule } from "@/components/ScheduleModal";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { BrandLogo } from "@/components/BrandLogo";

const JCI_STRENGTHS = [
  "Personal mentorship from John Campbell",
  "Relationship-first agency culture",
  "One-on-one coaching",
  "Business planning",
  "Accountability",
  "Faith-driven servant leadership",
  "Local leadership and accessibility",
  "Build your own business — not just a sales job",
];

const PARTNER_RESOURCES = [
  "Industry-leading training",
  "Carrier contracting assistance",
  "Access to multiple A-rated insurance companies",
  "Marketing systems",
  "Technology platform",
  "Lead programs",
  "National conferences and events",
  "Business-building resources",
  "Nationwide support network",
];

const CARRIERS = [
  { name: "Mutual of Omaha", file: "mutual-of-omaha.png", logo: true },
  { name: "Foresters Financial", file: "foresters-financial.png", logo: true },
  { name: "Americo", file: "americo.png", logo: false },
  { name: "Transamerica", file: "transamerica.png", logo: true },
  { name: "GPM Life", file: "gpm-life.png", logo: false },
  { name: "American Amicable", file: "american-amicable.png", logo: false },
  { name: "Ethos", file: "ethos.png", logo: true },
  { name: "Ameritas", file: "ameritas.png", logo: false },
  { name: "Gerber Life", file: "gerber-life.png", logo: false },
  { name: "Aetna / CVS Health", file: "aetna-cvs-health.png", logo: true },
];

const RECRUITING_BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/agent-career-conversation";
const RECRUITING_MODAL_TITLE = "Schedule a Career Discovery Call";

const VALUES = [
  {
    n: "01",
    title: "Independence, backed by a powerhouse.",
    body:
      "Build your own agency with the freedom of an independent broker — supported by the training, carriers, and technology of Equis Financial behind you.",
  },
  {
    n: "02",
    title: "Real mentorship, not a sink-or-swim desk.",
    body:
      "You'll be coached directly on product, process, and client care — so you can serve families with genuine confidence from day one.",
  },
  {
    n: "03",
    title: "Uncapped, purpose-driven income.",
    body:
      "Protecting families is meaningful work that rewards effort. Your growth is defined by the value you create, not a ceiling someone else sets.",
  },
  {
    n: "04",
    title: "A brand that earns trust.",
    body:
      "Represent a name built on clarity, integrity, and doing right by people — the kind of reputation that opens doors and keeps clients for life.",
  },
];

const usd = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const COMMISSION_PRESETS = [
  { id: "new", label: "New to the Industry", pct: 70 },
  { id: "growing", label: "Growing Agency", pct: 90 },
  { id: "established", label: "Established Producer", pct: 105 },
  { id: "custom", label: "Custom", pct: null },
];

function AnimatedNumber({ value, format }) {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    const controls = animate(prev.current, value, {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v),
    });
    prev.current = value;
    return () => controls.stop();
  }, [value]);
  return <>{format(display)}</>;
}

function GoldSlider({ value, onChange, min, max, step }) {
  return (
    <SliderPrimitive.Root
      className="relative flex w-full touch-none select-none items-center py-2 cursor-pointer"
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => onChange(v[0])}
    >
      <SliderPrimitive.Track className="relative h-[3px] w-full grow overflow-hidden rounded-full bg-white/12">
        <SliderPrimitive.Range className="absolute h-full bg-[#d4af37]" />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb
        className="block h-4 w-4 rounded-full bg-[#d4af37] shadow-[0_0_0_4px_rgba(212,175,55,0.18)] outline-none transition-transform duration-200 hover:scale-110 focus-visible:ring-2 focus-visible:ring-[#d4af37]/60"
        aria-label="Adjust value"
      />
    </SliderPrimitive.Root>
  );
}

function CommissionTooltip() {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        data-testid="calc-commission-info"
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="h-6 w-6 rounded-full border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] hover:bg-[#d4af37]/10 transition-colors duration-300"
        aria-label="About commission contract levels"
      >
        <Info size={12} />
      </button>
      <motion.div
        initial={false}
        animate={open ? { opacity: 1, y: 0, pointerEvents: "auto" } : { opacity: 0, y: 6, pointerEvents: "none" }}
        transition={{ duration: 0.25 }}
        data-testid="calc-commission-tooltip"
        className="absolute left-0 top-8 z-30 w-[300px] sm:w-[360px] bg-[#0a0a0a] border border-[#d4af37]/25 rounded-sm p-6 shadow-[0_30px_70px_rgba(0,0,0,0.7)]"
      >
        <p className="mono text-[10px] uppercase tracking-[0.25em] text-[#d4af37] mb-4">Commission Contract Levels</p>
        <div className="space-y-3 text-[#a3a3a3] text-xs leading-relaxed tracking-wide">
          <p>The commission percentages shown in this planner are illustrative examples only.</p>
          <p>Individual contract levels vary based on factors such as experience, production history, training, agency affiliation, and carrier agreements.</p>
          <p>Your specific contract level will be discussed during the interview and contracting process.</p>
          <p>This planner is intended to demonstrate how production and commission percentages work together when building an independent insurance business.</p>
        </div>
      </motion.div>
    </span>
  );
}

function BusinessCalculator() {
  const { openSchedule } = useSchedule();
  const [families, setFamilies] = useState(10);
  const [premium, setPremium] = useState(900);
  const [commission, setCommission] = useState(105);
  const [activePreset, setActivePreset] = useState("established");

  const applyPreset = (p) => {
    if (p.id === "custom") { setActivePreset("custom"); return; }
    setCommission(p.pct);
    setActivePreset(p.id);
  };
  const onCommission = (v) => { setCommission(v); setActivePreset("custom"); };

  const monthlyPremium = families * premium;
  const annualPremium = monthlyPremium * 12;
  const firstYearCommission = annualPremium * (commission / 100);
  const monthlyCommission = firstYearCommission / 12;
  const familiesYear = families * 12;

  const MAX_ANNUAL = 20 * 3000 * 12;
  const clamp = (x) => Math.min(1, Math.max(0, x));
  const usdFmt = (n) => usd(n);
  const intFmt = (n) => Math.round(n).toLocaleString("en-US");
  const pctFmt = (n) => `${Math.round(n)}%`;

  const results = [
    {
      icon: ShieldCheck,
      value: annualPremium,
      format: usdFmt,
      label: "Annual Premium Placed",
      note: "Total annual premium placed through your clients each year.",
      progress: clamp(annualPremium / MAX_ANNUAL),
    },
    {
      icon: Percent,
      value: commission,
      format: pctFmt,
      label: "Illustrative Commission Contract",
      note: "Illustrative contract percentage used for this example.",
      progress: clamp((commission - 70) / 70),
    },
    {
      icon: TrendingUp,
      value: firstYearCommission,
      format: usdFmt,
      label: "Estimated First-Year Commission",
      note: "Illustrative estimate based on your selected assumptions.",
      progress: clamp(firstYearCommission / (MAX_ANNUAL * 1.4)),
    },
    {
      icon: Wallet,
      value: monthlyCommission,
      format: usdFmt,
      label: "Illustrative Monthly Commission",
      note: "Illustrative monthly commission before business expenses, taxes, chargebacks, and other adjustments.",
      progress: clamp(monthlyCommission / (MAX_ANNUAL * 1.4 / 12)),
    },
    {
      icon: Users,
      value: familiesYear,
      format: intFmt,
      label: "Families Protected Per Year",
      note: "The number of households you could help protect each year.",
      progress: clamp(familiesYear / (20 * 12)),
      emphasis: true,
    },
  ];

  const snapshot = [
    { value: familiesYear, format: intFmt, label: "families protected each year" },
    { value: annualPremium, format: usdFmt, label: "annual premium placed" },
    { value: firstYearCommission, format: usdFmt, label: "estimated first-year commission" },
    { value: monthlyCommission, format: usdFmt, label: "illustrative monthly commission" },
  ];

  return (
    <section data-testid="join-calculator" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-8">
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Build Your Vision</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <Reveal className="mb-6 max-w-3xl">
          <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
            What kind of business <span className="italic text-[#d4af37]">can you build</span>?
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mb-14 max-w-2xl">
          <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide">
            Every successful agency is built one family at a time. Adjust the assumptions below to explore how
            consistently serving families can grow into a meaningful business built on relationships, service, and
            long-term income.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-10 lg:gap-16 items-start">
          {/* Controls */}
          <Reveal>
            <div className="bg-[#0a0a0a] border border-white/10 rounded-sm p-8 lg:p-12 space-y-12">
              {/* Families per month */}
              <div data-testid="calc-families-control">
                <div className="flex items-end justify-between mb-5">
                  <span className="mono text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Families Protected Per Month</span>
                  <span className="serif text-3xl text-[#f4f0ea] tabular-nums" data-testid="calc-families-value">{families}</span>
                </div>
                <GoldSlider value={families} onChange={setFamilies} min={1} max={20} step={1} />
                <div className="flex justify-between mono text-[9px] uppercase tracking-[0.2em] text-[#a3a3a3]/50 mt-3">
                  <span>1</span><span>20</span>
                </div>
              </div>

              {/* Average premium */}
              <div data-testid="calc-premium-control">
                <div className="flex items-end justify-between mb-5">
                  <span className="mono text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Average Annual Premium Per Household</span>
                  <span className="serif text-3xl text-[#f4f0ea] tabular-nums" data-testid="calc-premium-value">{usd(premium)}</span>
                </div>
                <GoldSlider value={premium} onChange={setPremium} min={600} max={3000} step={100} />
                <div className="flex justify-between mono text-[9px] uppercase tracking-[0.2em] text-[#a3a3a3]/50 mt-3">
                  <span>{usd(600)}</span><span>{usd(3000)}</span>
                </div>
              </div>

              {/* Illustrative commission contract */}
              <div data-testid="calc-commission-control">
                <div className="flex items-center gap-2 mb-5">
                  <span className="mono text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Illustrative Commission Contract</span>
                  <CommissionTooltip />
                  <span className="serif text-3xl text-[#f4f0ea] tabular-nums ml-auto" data-testid="calc-commission-value">{commission}%</span>
                </div>

                {/* Quick presets */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  {COMMISSION_PRESETS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => applyPreset(p)}
                      data-testid={`calc-preset-${p.id}`}
                      className={`mono text-[9px] leading-tight uppercase tracking-[0.12em] py-3 px-2 rounded-full border transition-colors duration-300 ${
                        activePreset === p.id
                          ? "bg-[#d4af37] text-[#030303] border-[#d4af37]"
                          : "border-white/15 text-[#a3a3a3] hover:border-[#d4af37]/50 hover:text-[#f4f0ea]"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

                <GoldSlider value={commission} onChange={onCommission} min={70} max={140} step={5} />
                <div className="flex justify-between mono text-[9px] uppercase tracking-[0.2em] text-[#a3a3a3]/50 mt-3">
                  <span>70%</span><span>140%</span>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Results */}
          <Reveal delay={0.12}>
            <div className="grid sm:grid-cols-2 gap-5">
              {results.map((r, i) => (
                <div
                  key={r.label}
                  data-testid={`calc-result-${i}`}
                  className="relative bg-gradient-to-b from-[#111] to-[#0a0a0a] border border-white/10 rounded-sm p-8 overflow-hidden flex flex-col"
                >
                  {/* Progress accent */}
                  <div className="absolute top-0 left-0 right-0 h-px bg-white/5">
                    <motion.div
                      className="h-full bg-[#d4af37]"
                      animate={{ width: `${r.progress * 100}%` }}
                      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>
                  <span className={`rounded-full border flex items-center justify-center mb-6 ${r.emphasis ? "h-12 w-12 border-[#d4af37]/55" : "h-11 w-11 border-[#d4af37]/30"}`}>
                    <r.icon size={r.emphasis ? 21 : 18} className="text-[#d4af37]" />
                  </span>
                  <div className={`serif font-light text-[#f4f0ea] leading-none tabular-nums ${r.emphasis ? "text-4xl lg:text-[2.6rem]" : "text-3xl lg:text-4xl"}`}>
                    <AnimatedNumber value={r.value} format={r.format} />
                  </div>
                  <p className="mono text-[10px] uppercase tracking-[0.2em] text-[#f4f0ea]/85 mt-4 leading-relaxed">{r.label}</p>
                  <p className="text-[#a3a3a3]/70 text-xs leading-relaxed tracking-wide mt-2">{r.note}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Business Snapshot */}
        <Reveal className="mt-8">
          <div data-testid="calc-snapshot" className="relative bg-gradient-to-b from-[#111] to-[#0a0a0a] border border-[#d4af37]/20 rounded-sm p-8 lg:p-12 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-[#d4af37]/40" />
            <div className="flex items-center gap-3 mb-8">
              <span className="serif text-2xl lg:text-3xl text-[#f4f0ea]">Your Agency Snapshot</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide max-w-3xl">
              Helping <span className="text-[#f4f0ea]">{families} families</span> each month · Average Annual Premium{" "}
              <span className="text-[#f4f0ea]">{usd(premium)}</span> per household · Illustrative Commission Contract{" "}
              <span className="text-[#f4f0ea]">{commission}%</span>.
            </p>
            <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide mt-3 mb-10">
              Based on these planning assumptions, your independent agency could look like this:
            </p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-8">
              {snapshot.map((s) => (
                <div key={s.label}>
                  <div className="serif font-light text-[#d4af37] text-3xl lg:text-4xl leading-none tabular-nums">
                    <AnimatedNumber value={s.value} format={s.format} />
                  </div>
                  <p className="text-[#a3a3a3] text-xs leading-relaxed tracking-wide mt-3">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* How Compensation Works */}
        <Reveal className="mt-8">
          <div data-testid="calc-education" className="bg-[#0a0a0a] border border-white/10 rounded-sm p-8 lg:p-12">
            <h3 className="serif text-2xl lg:text-3xl text-[#f4f0ea] mb-6">How Compensation Works</h3>
            <div className="space-y-5 text-[#a3a3a3] text-base leading-relaxed tracking-wide max-w-3xl">
              <p>
                Independent insurance advisors are generally compensated based on the annual premium of the policies
                they help place and their commission contract level.
              </p>
              <p>
                As advisors gain experience, complete training, consistently serve more families, and increase
                production, they may become eligible for higher commission contract levels.
              </p>
              <p>
                This planner is intended to illustrate how production and commission percentages work together when
                building a long-term independent insurance agency. Individual results vary based on licensing, effort,
                carrier contracts, product mix, persistency, and other business factors.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Disclaimer */}
        <Reveal delay={0.05}>
          <p data-testid="calc-disclaimer" className="text-[#a3a3a3]/55 text-xs leading-relaxed tracking-wide mt-8 max-w-4xl">
            Income illustrations are provided solely for educational purposes and should not be interpreted as
            guarantees, promises, or representations of future earnings. Actual production and compensation vary based on
            licensing, individual effort, carrier contracts, product mix, persistency, business expenses, market
            conditions, and many other factors.
          </p>
        </Reveal>

        {/* CTA */}
        <Reveal delay={0.1}>
          <div className="mt-10 pt-10 border-t border-white/10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <h3 className="serif text-2xl lg:text-3xl text-[#f4f0ea] mb-3">
                Curious how these numbers are <span className="italic text-[#d4af37]">calculated</span>?
              </h3>
              <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide">
                Schedule a conversation with John Campbell to learn how independent advisors build successful agencies,
                how commission contracts work, and whether this opportunity is the right fit for you.
              </p>
            </div>
            <button
              onClick={() => openSchedule(RECRUITING_BOOKING_URL, RECRUITING_MODAL_TITLE)}
              data-testid="calc-schedule-btn"
              className="group shrink-0 inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] rounded-full hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400"
            >
              <CalendarDays size={15} /> Schedule an Introductory Conversation
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PartnershipStory() {
  return (
    <>
      {/* Supported by Industry-Leading Resources */}
      <section data-testid="join-partnership" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">The Best of Both Worlds</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <Reveal className="mb-6 max-w-3xl">
            <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
              Supported by <span className="italic text-[#d4af37]">industry-leading</span> resources.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mb-14 max-w-3xl">
            <div className="space-y-5 text-[#a3a3a3] text-base leading-relaxed tracking-wide">
              <p>
                When you join John Campbell Insurance, you're joining a relationship-first agency committed to helping
                agents build businesses that matter.
              </p>
              <p>
                You'll receive direct mentorship, coaching, and support from John Campbell while also benefiting from the
                national resources available through our partnership with Equis Financial — <span className="text-[#f4f0ea]">"The Agent's Company"</span> — an
                Integrity Marketing Group company.
              </p>
              <p>
                This partnership provides access to industry-leading training, technology, carrier relationships, marketing
                resources, business systems, and one of the most respected organizations in the insurance industry.
              </p>
              <p className="text-[#f4f0ea]">
                The result is the best of both worlds: a personal mentor who is invested in your success, backed by the
                strength and resources of a nationally recognized organization.
              </p>
            </div>
          </Reveal>

          {/* Two-column comparison */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* JCI — primary */}
            <Reveal>
              <div className="relative h-full bg-gradient-to-b from-[#141414] to-[#0a0a0a] border border-[#d4af37]/35 rounded-sm p-8 lg:p-12 overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-px bg-[#d4af37]/50" />
                <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">Your Foundation</span>
                <h3 className="serif text-2xl lg:text-[2rem] leading-tight text-[#f4f0ea] mt-4 mb-8">
                  Build your business with John Campbell Insurance
                </h3>
                <ul className="space-y-4">
                  {JCI_STRENGTHS.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="shrink-0 mt-0.5 h-6 w-6 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center">
                        <Check size={13} className="text-[#d4af37]" />
                      </span>
                      <span className="text-[#f4f0ea]/90 leading-relaxed tracking-wide text-sm lg:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Partner resources — supporting */}
            <Reveal delay={0.1}>
              <div className="relative h-full bg-[#0a0a0a] border border-white/10 rounded-sm p-8 lg:p-12">
                <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3]">National Backing</span>
                <h3 className="serif text-2xl lg:text-[2rem] leading-tight text-[#f4f0ea] mt-4 mb-8">
                  Supported through Equis Financial &amp; Integrity
                </h3>
                <ul className="space-y-4">
                  {PARTNER_RESOURCES.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="shrink-0 mt-0.5 h-6 w-6 rounded-full border border-white/15 flex items-center justify-center">
                        <Check size={13} className="text-[#a3a3a3]" />
                      </span>
                      <span className="text-[#f4f0ea]/80 leading-relaxed tracking-wide text-sm lg:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* My Story — Why I Chose Equis + Visual Credibility */}
      <section data-testid="join-my-story" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">My Story</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <Reveal className="mb-10 max-w-3xl">
            <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
              Why I chose <span className="italic text-[#d4af37]">Equis Financial</span>.
            </h2>
          </Reveal>

          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-20 items-start">
            <Reveal delay={0.06}>
              <div className="space-y-6 text-[#a3a3a3] text-lg leading-relaxed tracking-wide max-w-2xl">
                <p>
                  When I entered the insurance industry, I had a choice about where I wanted to build my business. Like most
                  new agents, I wasn't just looking for a company — I was looking for the right people, the right culture,
                  and the right opportunity.
                </p>
                <p>
                  Building a successful insurance business isn't about chasing the next sale — it's about consistently serving
                  people well, doing what's right for clients, and earning their trust.
                </p>
                <p>
                  I'm grateful to have been recognized as a back-to-back Elite Producer with Equis Financial in both 2024 and
                  2025, but what I'm most proud of is the opportunity to help hundreds of individuals and families protect what
                  matters most while building a business rooted in integrity and relationships.
                </p>
                <p>
                  Those experiences have shaped how I lead John Campbell Insurance today, and they're the same principles I
                  want to pass on to every agent who joins our team.
                </p>
                <p>
                  After careful consideration, I chose to partner with Equis Financial because of their commitment to
                  training, technology, mentorship, integrity, and helping independent agents build long-term businesses.
                  <span className="text-[#f4f0ea]"> That decision changed my life.</span>
                </p>
                <p>
                  Through Equis Financial and the resources provided by Integrity Marketing Group, I gained access to
                  outstanding training, business systems, carrier relationships, technology, and a nationwide network of
                  professionals who genuinely wanted to see agents succeed.
                </p>
                <p>
                  Those resources helped me build John Campbell Insurance into the agency it is today. Now, my passion is
                  helping other agents do the same.
                </p>
                <p>
                  When you join John Campbell Insurance, you're not simply joining another insurance agency. You're joining a
                  relationship-first organization where you'll receive personal mentorship from someone who has walked the
                  path before you — while also benefiting from the same national resources, education, and support that
                  helped me build my own business.
                </p>
                <p>
                  I don't believe success happens by accident. I believe it happens when good people are surrounded by the
                  right leadership, the right systems, and the right opportunities.
                  <span className="text-[#f4f0ea]"> That's exactly what I want to provide for every agent who joins our team.</span>
                </p>
              </div>
            </Reveal>

            {/* Closing quote */}
            <Reveal delay={0.12} className="lg:sticky lg:top-28">
              <figure className="relative bg-[#0a0a0a] border border-[#d4af37]/25 rounded-sm p-8 lg:p-12">
                <div className="absolute top-0 left-0 right-0 h-px bg-[#d4af37]/40" />
                <Quote size={40} className="text-[#d4af37]/40 mb-5" />
                <blockquote className="serif italic text-2xl lg:text-3xl font-light leading-snug text-[#f4f0ea] text-balance">
                  “I can't promise this business is easy. But I can promise you won't have to build it alone.”
                </blockquote>
                <figcaption className="mt-8">
                  <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">— John Campbell</div>
                  <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#a3a3a3] mt-1">Founder, John Campbell Insurance</div>
                </figcaption>
              </figure>
            </Reveal>
          </div>

          {/* Visual Credibility — partner lockups */}
          <Reveal className="mt-16 lg:mt-24">
            <div data-testid="join-partners" className="bg-gradient-to-b from-[#0a0a0a] to-[#050505] border border-white/10 rounded-sm px-6 py-14 lg:px-16 lg:py-20">
              {/* JCI — primary / hero */}
              <div className="flex flex-col items-center text-center">
                <BrandLogo size="lg" />
                <p className="mono text-[9px] uppercase tracking-[0.35em] text-[#a3a3a3] mt-6 max-w-md leading-relaxed">
                  Helping Agents Build Businesses That Matter
                </p>
              </div>

              {/* divider */}
              <div className="flex items-center justify-center my-12 lg:my-16">
                <span className="h-px w-16 bg-white/10" />
                <span className="mono text-[9px] uppercase tracking-[0.3em] text-[#737373] px-5">Proudly partnered with</span>
                <span className="h-px w-16 bg-white/10" />
              </div>

              {/* Official Equis Financial — An Integrity Company logo */}
              <div className="flex flex-col items-center text-center">
                <img
                  src="/brand/partners/equis-integrity-light.png"
                  alt="Equis Financial — An Integrity Company"
                  className="w-full max-w-[360px] lg:max-w-[440px] h-auto"
                  width={960}
                  height={352}
                  loading="lazy"
                  decoding="async"
                />
                <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37] mt-8">"The Agent's Company."</p>
              </div>

              <p className="text-[#a3a3a3]/70 text-sm leading-relaxed tracking-wide text-center max-w-2xl mx-auto mt-12">
                Providing the national resources, technology, training, and carrier relationships that help independent
                agents build successful businesses.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function EquisVideo() {
  const [loaded, setLoaded] = useState(false);
  const VIDEO_ID = "HSREVFk7f0E";
  const play = () => setLoaded(true);
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); play(); }
  };

  return (
    <section data-testid="join-equis-video" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-8">
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Company Overview</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <Reveal className="mb-6 max-w-3xl">
          <h2 className="serif font-light leading-[1.05] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
            See why I chose <span className="italic text-[#d4af37]">Equis Financial</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mb-12 max-w-3xl">
          <div className="space-y-5 text-[#a3a3a3] text-base leading-relaxed tracking-wide">
            <p>
              When I decided to build John Campbell Insurance, I wanted more than just another insurance company — I wanted
              a platform that would allow me to serve clients with integrity while helping other agents build meaningful,
              rewarding careers.
            </p>
            <p>
              I'd like to invite you to watch this brief overview to see why I chose Equis Financial as the platform behind
              John Campbell Insurance — and how it can help you build a business you're proud of.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="max-w-4xl mx-auto">
            <div className="relative aspect-video overflow-hidden rounded-lg border border-white/10 bg-[#0a0a0a] shadow-[0_40px_90px_rgba(0,0,0,0.6)]">
              {loaded ? (
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1&cc_load_policy=1`}
                  title="Equis Financial — Company Overview"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Play the Equis Financial company overview video"
                  data-testid="equis-video-play"
                  onClick={play}
                  onKeyDown={onKey}
                  className="group absolute inset-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d4af37]/70"
                >
                  <img
                    src="/brand/partners/equis-video-thumb.jpg"
                    alt="Equis Financial — The Agent's Company, an Integrity Marketing Group company"
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                    decoding="async"
                    width={1280}
                    height={720}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030303]/70 via-[#030303]/20 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex items-center justify-center h-20 w-20 lg:h-24 lg:w-24 rounded-full bg-[#030303]/50 border border-[#d4af37]/60 backdrop-blur-sm transition-all duration-400 group-hover:bg-[#d4af37] group-hover:scale-105">
                      <Play size={30} className="text-[#d4af37] group-hover:text-[#030303] transition-colors duration-400 ml-1" fill="currentColor" />
                    </span>
                  </div>
                  <div className="absolute bottom-5 left-6 right-6">
                    <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#f4f0ea]/90">Watch · Equis Financial Overview</span>
                  </div>
                </div>
              )}
            </div>
            <p className="mono text-[9px] uppercase tracking-[0.3em] text-[#737373] text-center mt-6">
              Equis Financial · "The Agent's Company" · An Integrity Marketing Group Company
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CarrierCredibility() {
  return (
    <section data-testid="join-carriers" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-8">
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Independence &amp; Choice</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <Reveal className="mb-6 max-w-4xl">
          <h2 className="serif font-light leading-[1.05] text-[#f4f0ea] text-3xl sm:text-4xl lg:text-5xl">
            Independent advice. <span className="italic text-[#d4af37]">Industry-leading</span> insurance companies.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mb-14 max-w-3xl">
          <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide">
            As an independent agent with John Campbell Insurance, you'll have access to a diverse portfolio of products
            through our partnership with Equis Financial — allowing you to recommend solutions based on what's truly in your
            clients' best interests, not a single company's product lineup. You'll have access to many of the nation's most
            recognized and financially respected insurance companies, including:
          </p>
        </Reveal>

        {/* Carrier logo grid */}
        <Reveal delay={0.05}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
            {CARRIERS.map((c) => (
              <div
                key={c.name}
                data-testid={`carrier-${c.file.replace(".png", "")}`}
                className="group flex items-center justify-center h-24 lg:h-28 px-5 rounded-md bg-[#f4f0ea] border border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.35)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.5)] transition-shadow duration-400"
              >
                {c.logo ? (
                  <img
                    src={`/brand/partners/carriers/${c.file}`}
                    alt={`${c.name} — insurance carrier available through John Campbell Insurance`}
                    className="max-h-11 lg:max-h-12 w-auto max-w-[82%] object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <span className="serif text-center text-[#1a1a1a]/75 text-base lg:text-lg leading-tight tracking-tight">
                    {c.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-12 max-w-3xl">
          <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide">
            Along with access to many other respected insurance companies, you'll have the flexibility to recommend
            solutions based on what's truly in your clients' best interests — not a limited product lineup.
            <span className="text-[#f4f0ea]"> That's one of the greatest advantages of being an independent agent with John
            Campbell Insurance.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function PersonalInvitation({ openSchedule }) {
  return (
    <section data-testid="join-personal-invitation" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-8">
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">A Personal Invitation</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-20 items-start">
          <Reveal>
            <div className="space-y-6 text-[#a3a3a3] text-lg leading-relaxed tracking-wide max-w-2xl">
              <p>
                If you're looking for a place where you'll be more than just an agent number, I'd love the opportunity to
                get to know you.
              </p>
              <p>
                Whether you're new to the insurance industry or an experienced producer looking for a better environment,
                let's have a conversation about your goals and see if John Campbell Insurance is the right fit for you.
              </p>
              <p className="text-[#f4f0ea]">No pressure. No obligation. Just an honest conversation about your future.</p>
              <div className="pt-2">
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">— John Campbell</div>
                <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#a3a3a3] mt-1">Founder, John Campbell Insurance</div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="bg-gradient-to-b from-[#141414] to-[#0a0a0a] border border-[#d4af37]/25 rounded-sm p-8 lg:p-12">
              <div className="h-12 w-12 rounded-full border border-[#d4af37]/40 bg-[#0a0a0a] flex items-center justify-center mb-6">
                <Compass size={20} className="text-[#d4af37]" />
              </div>
              <h3 className="serif text-2xl lg:text-3xl text-[#f4f0ea] mb-4">Let's start a conversation.</h3>
              <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide mb-8">
                Book a relaxed, one-on-one introductory chat with John. No scripts, no pressure — just a genuine
                conversation about where you want to go.
              </p>
              <button
                onClick={() => openSchedule(RECRUITING_BOOKING_URL, RECRUITING_MODAL_TITLE)}
                data-testid="invitation-schedule-btn"
                className="group w-full inline-flex items-center justify-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] rounded-full hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400"
              >
                <CalendarDays size={15} /> Schedule an Introductory Conversation
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function JoinContent() {
  const { openSchedule } = useSchedule();

  return (
    <main>
      {/* Hero */}
      <section data-testid="join-hero" className="relative min-h-[70vh] flex flex-col justify-end pb-16 lg:pb-24 px-6 lg:px-10 max-w-[1400px] mx-auto pt-40">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[#d4af37]" />
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Careers · Now Hiring</span>
        </motion.div>

        <Reveal>
          <h1 className="serif font-light leading-[0.95] text-[#f4f0ea] text-[13vw] sm:text-[11vw] lg:text-[7.5vw]">
            Join Our <span className="italic text-[#d4af37]">Team</span>.<br />Build a career that matters.
          </h1>
        </Reveal>

        <Reveal delay={0.12}>
          <p className="serif italic text-[#d4af37] text-xl sm:text-2xl mt-6">
            Helping Agents Build Businesses That Matter.
          </p>
        </Reveal>

        <Reveal delay={0.2} className="mt-10 max-w-xl">
          <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide">
            We're looking for driven, people-first individuals who want to help families protect what matters most —
            with the independence to build their own book of business and the support to do it well.
          </p>
        </Reveal>

        <Reveal delay={0.3} className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <a
            href="/join/apply"
            data-testid="join-apply-btn"
            className="group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 bg-[#f4f0ea] text-[#030303] rounded-full hover:bg-[#d4af37] transition-colors duration-400"
          >
            Apply now
            <ArrowRight size={15} className="transition-transform duration-400 group-hover:translate-x-1" />
          </a>
          <button
            onClick={() => openSchedule(RECRUITING_BOOKING_URL, RECRUITING_MODAL_TITLE)}
            data-testid="join-schedule-btn"
            className="group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] rounded-full hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400"
          >
            <CalendarDays size={15} /> Schedule a chat
          </button>
        </Reveal>
      </section>

      {/* Meet Your Mentor */}
      <section data-testid="join-mentor" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Meet Your Mentor</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <Reveal className="mb-6 max-w-3xl">
            <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
              People Join <span className="italic text-[#d4af37]">Leaders</span>. Not Companies.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mb-16 max-w-2xl">
            <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide">
              The opportunity matters. The compensation matters. But the person you'll learn from matters even more.
              At John Campbell Insurance, you'll work directly with someone who is actively serving clients, continually
              improving, and committed to helping you build a business you can be proud of.
            </p>
          </Reveal>

          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-20 items-start">
            {/* Left — headshot */}
            <Reveal className="lg:sticky lg:top-28">
              <div className="relative">
                <div className="absolute -inset-3 rounded-2xl border border-[#d4af37]/20" />
                <div className="relative overflow-hidden rounded-2xl shadow-[0_40px_90px_rgba(0,0,0,0.65)] bg-gradient-to-b from-[#161616] to-[#0a0a0a]">
                  <img
                    src="https://customer-assets-jai6qajn.emergentagent.net/job_insurance-vr-site/artifacts/0ncqvkyk_Headshot.png"
                    alt="John Campbell — your mentor"
                    className="w-full h-[440px] lg:h-[600px] object-cover object-top"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(80% 60% at 50% 15%, transparent 45%, rgba(3,3,3,0.5) 100%)" }} />
                  <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                </div>
                <div className="absolute bottom-5 left-6">
                  <div className="serif text-2xl text-[#f4f0ea]">John Campbell</div>
                  <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#d4af37] mt-1">Founder &amp; Mentor</div>
                </div>
              </div>
            </Reveal>

            {/* Right — biography */}
            <div>
              <Reveal>
                <h3 className="serif text-3xl lg:text-4xl text-[#f4f0ea] mb-8">Meet John Campbell</h3>
              </Reveal>
              <Reveal delay={0.06}>
                <div className="space-y-6 text-[#a3a3a3] text-lg leading-relaxed tracking-wide max-w-xl">
                  <p>
                    Helping families protect what matters most isn't simply what I do. It's the mission that drives everything I build.
                  </p>
                  <p>
                    Over the years I've had the privilege of helping families across multiple states protect their futures through
                    personalized life insurance solutions designed around their unique needs.
                  </p>
                  <p>
                    John Campbell Insurance was built on a simple belief: <span className="text-[#f4f0ea]">people deserve honest advice,
                    genuine relationships, and an advisor who puts their family's best interests first.</span>
                  </p>
                  <p>
                    That same philosophy guides how I mentor new advisors. If you join our team, you won't be handed a script and left
                    to figure everything out on your own. You'll receive personal coaching, practical training, and the support needed
                    to confidently serve families while building a business of your own.
                  </p>
                  <p>
                    Whether you're new to insurance or already licensed, my goal is to help you become the kind of advisor clients trust
                    for years—not just during the sale, but throughout every stage of life.
                  </p>
                </div>
              </Reveal>

              {/* Pull quote */}
              <Reveal delay={0.1}>
                <figure className="relative mt-12 bg-[#0a0a0a] border border-white/10 rounded-sm p-8 lg:p-12">
                  <Quote size={40} className="text-[#d4af37]/40 mb-4" />
                  <blockquote className="serif italic text-2xl lg:text-3xl font-light leading-snug text-[#f4f0ea] text-balance">
                    “I'm not looking for the most experienced people. I'm looking for people of integrity who are willing to learn,
                    serve others, and build something they can be proud of.”
                  </blockquote>
                  <figcaption className="mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37] mt-8">— John Campbell</figcaption>
                </figure>
              </Reveal>
            </div>
          </div>

          {/* What You Can Expect */}
          <Reveal className="mt-16">
            <div className="bg-[#0a0a0a] border border-white/10 rounded-sm p-8 lg:p-12">
              <div className="flex items-center gap-3 mb-10">
                <span className="serif text-2xl lg:text-3xl text-[#f4f0ea]">What You Can Expect</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
                {[
                  [UserCheck, "Personal mentorship from John Campbell"],
                  [CalendarCheck, "Weekly coaching and accountability"],
                  [MessagesSquare, "Training built around real client conversations"],
                  [Building2, "Access to multiple respected insurance carriers"],
                  [Sparkles, "Modern technology including AI tools and automation"],
                  [HeartHandshake, "A relationship-focused culture built on integrity, service and professionalism"],
                ].map(([Icon, text]) => (
                  <div key={text} className="flex items-start gap-4">
                    <span className="shrink-0 mt-0.5 h-10 w-10 rounded-full border border-[#d4af37]/30 flex items-center justify-center">
                      <Icon size={17} className="text-[#d4af37]" />
                    </span>
                    <p className="text-[#f4f0ea]/85 leading-relaxed tracking-wide text-sm">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why join */}
      <section className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Why Build With Us</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <Reveal className="mb-10 max-w-3xl">
            <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
              A career with <span className="italic text-[#d4af37]">independence</span> & impact.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-x-16">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.08} className="grid grid-cols-[auto_1fr] gap-6 lg:gap-10 py-10 border-t border-white/10">
                <span className="mono text-sm text-[#d4af37] pt-2">{v.n}</span>
                <div>
                  <h3 className="serif text-2xl lg:text-3xl text-[#f4f0ea] mb-3">{v.title}</h3>
                  <p className="text-[#a3a3a3] leading-relaxed tracking-wide">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What kind of business can you build */}
      <BusinessCalculator />

      {/* Supported by resources + My Story + partner credibility */}
      <PartnershipStory />

      {/* Official Equis Financial company overview video */}
      <EquisVideo />

      {/* Carrier credibility grid */}
      <CarrierCredibility />

      {/* Personal invitation */}
      <PersonalInvitation openSchedule={openSchedule} />

      {/* Closing CTA */}
      <section className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <Reveal>
            <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37] block mb-4">Ready to start?</span>
            <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
              Let's talk about your <span className="italic text-[#d4af37]">future</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="flex items-center gap-5">
            <a
              href="/join/apply"
              data-testid="join-cta-apply"
              className="group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 bg-[#f4f0ea] text-[#030303] rounded-full hover:bg-[#d4af37] transition-colors duration-400"
            >
              Apply now
              <ArrowRight size={15} className="transition-transform duration-400 group-hover:translate-x-1" />
            </a>
            <a href="/" data-testid="join-back-home" className="mono text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#f4f0ea] link-underline">
              ← Back to home
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export default function Join() {
  return (
    <div className="App grain bg-[#030303]">
      <ScheduleProvider>
        <SmoothScroll>
          <Navbar />
          <JoinContent />
          <Footer />
        </SmoothScroll>
      </ScheduleProvider>
    </div>
  );
}
